import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, Package, MapPin, Gift, Users, LogOut, Loader2, RefreshCw, Plus, Trash2, Star, Truck,
} from "lucide-react";
import api, { formatMoney, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "orders", label: "Orders", icon: Package },
  { id: "addresses", label: "Addresses", icon: MapPin },
  { id: "rewards", label: "Rewards", icon: Gift, soon: true },
  { id: "referrals", label: "Referrals", icon: Users, soon: true },
];

const EMPTY_ADDR = { name: "", address1: "", address2: "", city: "", state: "", postal_code: "", country: "US", is_default: false };

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const tone = s.includes("complete") || s.includes("paid") || s.includes("on_hold") || s.includes("processing")
    ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
    : s.includes("cancel") || s.includes("refund") ? "bg-rose-500/15 text-rose-700 border-rose-500/30"
    : "bg-stone-500/15 text-stone-700 border-stone-500/30";
  return <span className={`inline-block px-2.5 py-1 pill text-[11px] font-semibold border capitalize ${tone}`}>{(status || "—").replace(/_/g, " ")}</span>;
}

export default function Account() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [orders, setOrders] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", newsletter: !!user?.newsletter });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [addrForm, setAddrForm] = useState(null); // null | EMPTY | existing

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/account/overview");
      setOverview(data);
      setProfile({ name: data.user.name || "", phone: data.user.phone || "", newsletter: !!data.user.newsletter });
      setProfileLoaded(true);
    } catch (e) { console.error("Failed to load account overview", e); }
  }, []);

  useEffect(() => { if (user && user.email) loadOverview(); }, [user, loadOverview]);
  useEffect(() => {
    if (!user || !user.email) return;
    if (section === "orders" && orders === null) api.get("/account/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    if (section === "addresses" && addresses === null) api.get("/account/addresses").then((r) => setAddresses(r.data.addresses)).catch(() => setAddresses([]));
  }, [section, user, orders, addresses]);

  if (!user) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Loading…</div>;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = { phone: profile.phone, newsletter: profile.newsletter };
      if (profile.name && profile.name.trim()) payload.name = profile.name.trim();
      const { data } = await api.patch("/account/profile", payload);
      setUser((u) => ({ ...u, ...data.user }));
      toast.success("Profile saved");
      loadOverview();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const refreshWoo = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/account/refresh-woo");
      setOrders(data.orders);
      toast.success("Order history refreshed");
      loadOverview();
    } catch { toast.error("Couldn't refresh from WooCommerce."); }
    finally { setRefreshing(false); }
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...addrForm };
      const { data } = addrForm.id
        ? await api.patch(`/account/addresses/${addrForm.id}`, payload)
        : await api.post("/account/addresses", payload);
      setAddresses(data.addresses);
      setAddrForm(null);
      toast.success("Address saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteAddress = async (id) => {
    try { const { data } = await api.delete(`/account/addresses/${id}`); setAddresses(data.addresses); }
    catch { toast.error("Couldn't delete address."); }
  };

  const setField = (k) => (e) => setAddrForm((a) => ({ ...a, [k]: e.target.value }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="account-page">
      <span className="eyebrow">My account</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">Welcome, <span className="gold-text">{(user.name || "Researcher").split(" ")[0]}</span></h1>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-10">
        {/* Left nav */}
        <aside className="lg:sticky lg:top-24 self-start min-w-0">
          <nav className="flex lg:flex-col gap-2 overflow-x-auto w-full min-w-0" data-testid="account-nav">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => !n.soon && setSection(n.id)} data-testid={`account-nav-${n.id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${section === n.id ? "btn-gold" : "bg-white border border-border hover:bg-secondary"} ${n.soon ? "opacity-60 cursor-default" : ""}`}>
                <n.icon className="w-4 h-4 shrink-0" /> {n.label}
                {n.soon && <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">Soon</span>}
              </button>
            ))}
            <button onClick={() => { logout(); navigate("/"); }} data-testid="account-logout" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-white border border-border hover:bg-destructive/10 hover:text-destructive transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </nav>
        </aside>

        {/* Content */}
        <div className="min-w-0">
          {section === "overview" && (
            <div className="space-y-6" data-testid="account-overview">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="card-soft rounded-2xl p-6"><p className="eyebrow">Orders</p><p className="font-display font-extrabold text-4xl mt-2 gold-text" data-testid="stat-order-count">{overview ? overview.stats.order_count : "—"}</p></div>
                <div className="card-soft rounded-2xl p-6"><p className="eyebrow">Lifetime spend</p><p className="font-display font-extrabold text-4xl mt-2 gold-text" data-testid="stat-lifetime">{overview ? formatMoney(overview.stats.lifetime_spend) : "—"}</p></div>
              </div>
              <form onSubmit={saveProfile} className="card-soft rounded-2xl p-6 sm:p-8 space-y-5" data-testid="account-profile-form">
                <h2 className="font-display font-bold text-lg">Profile</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-sm font-bold">Full name</label><Input value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} className="mt-2 h-11 bg-white" data-testid="profile-name" /></div>
                  <div><label className="text-sm font-bold">Phone</label><Input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} className="mt-2 h-11 bg-white" data-testid="profile-phone" /></div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input type="checkbox" checked={profile.newsletter} onChange={(e) => setProfile((p) => ({ ...p, newsletter: e.target.checked }))} className="accent-[#8a681c] w-4 h-4" data-testid="profile-newsletter" />
                  <span className="text-sm text-muted-foreground">Receive sale & new-product emails</span>
                </label>
                <button type="submit" disabled={savingProfile || !profileLoaded} className="h-11 px-6 rounded-xl btn-gold font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="profile-save">
                  {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />} Save profile
                </button>
              </form>
            </div>
          )}

          {section === "orders" && (
            <div className="card-soft rounded-2xl overflow-hidden" data-testid="account-orders">
              <div className="p-5 flex items-center justify-between border-b border-border">
                <h2 className="font-display font-bold text-lg">Order history</h2>
                <button onClick={refreshWoo} disabled={refreshing} className="pill px-3 py-1.5 text-xs font-semibold bg-white border border-border inline-flex items-center gap-2 hover:bg-secondary" data-testid="orders-refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
              {orders === null ? <p className="p-8 text-muted-foreground" data-testid="orders-loading">Loading orders…</p>
                : !orders.length ? <p className="p-8 text-center text-muted-foreground" data-testid="orders-empty">No orders yet. Your WooCommerce + new-site orders will appear here.</p>
                : <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="orders-table">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-[#f6f1e6]">
                      <th className="px-6 py-3 font-bold">Order</th><th className="px-6 py-3 font-bold">Date</th><th className="px-6 py-3 font-bold">Status</th><th className="px-6 py-3 font-bold">Total</th><th className="px-6 py-3 font-bold hidden sm:table-cell">Source</th><th className="px-6 py-3 font-bold">Tracking</th>
                    </tr></thead>
                    <tbody>
                      {orders.map((o, i) => (
                        <tr key={`${o.order_number}-${i}`} data-testid={`order-row-${o.order_number}`} className="border-b border-border last:border-0">
                          <td className="px-6 py-4 font-semibold">{o.order_number}</td>
                          <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">{o.date ? new Date(o.date).toLocaleDateString() : "—"}</td>
                          <td className="px-6 py-4"><StatusPill status={o.status} /></td>
                          <td className="px-6 py-4 font-display font-bold">{formatMoney(o.total)}</td>
                          <td className="px-6 py-4 hidden sm:table-cell text-xs text-muted-foreground">{o.source === "woocommerce" ? "WooCommerce" : "Ethos"}</td>
                          <td className="px-6 py-4">
                            {o.tracking ? (
                              <a href={o.tracking.url} target="_blank" rel="noopener noreferrer" data-testid={`order-track-${o.order_number}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "var(--gold)" }}>
                                <Truck className="w-3.5 h-3.5" /> {o.tracking.carrier_label}
                              </a>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>}
            </div>
          )}

          {section === "addresses" && (
            <div className="space-y-5" data-testid="account-addresses">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-lg">Shipping addresses</h2>
                <button onClick={() => setAddrForm({ ...EMPTY_ADDR })} className="pill px-3 py-1.5 text-xs font-semibold btn-gold inline-flex items-center gap-2" data-testid="address-add"><Plus className="w-3.5 h-3.5" /> Add address</button>
              </div>
              {addresses === null ? <p className="text-muted-foreground" data-testid="addresses-loading">Loading…</p>
                : !addresses.length && !addrForm ? <p className="card-soft rounded-2xl p-8 text-center text-muted-foreground" data-testid="addresses-empty">No saved addresses yet.</p>
                : <div className="grid sm:grid-cols-2 gap-4">
                    {(addresses || []).map((a) => (
                      <div key={a.id} className="card-soft rounded-2xl p-5" data-testid={`address-card-${a.id}`}>
                        <div className="flex items-start justify-between">
                          <p className="font-semibold">{a.name}</p>
                          {a.is_default && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8a681c]"><Star className="w-3 h-3 fill-current" /> Default</span>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{a.address1}{a.address2 ? `, ${a.address2}` : ""}<br />{a.city}, {a.state} {a.postal_code}<br />{a.country}</p>
                        <div className="flex gap-3 mt-4 text-xs">
                          <button onClick={() => setAddrForm(a)} className="font-semibold hover:underline" data-testid={`address-edit-${a.id}`} style={{ color: "var(--gold)" }}>Edit</button>
                          <button onClick={() => deleteAddress(a.id)} className="font-semibold text-destructive hover:underline inline-flex items-center gap-1" data-testid={`address-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>}
              {addrForm && (
                <form onSubmit={saveAddress} className="card-soft rounded-2xl p-6 space-y-4" data-testid="address-form">
                  <h3 className="font-display font-bold">{addrForm.id ? "Edit address" : "New address"}</h3>
                  <Input required placeholder="Full name" value={addrForm.name} onChange={setField("name")} className="h-11 bg-white" data-testid="addr-name" />
                  <Input required placeholder="Address line 1" value={addrForm.address1} onChange={setField("address1")} className="h-11 bg-white" data-testid="addr-address1" />
                  <Input placeholder="Address line 2 (optional)" value={addrForm.address2} onChange={setField("address2")} className="h-11 bg-white" data-testid="addr-address2" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Input required placeholder="City" value={addrForm.city} onChange={setField("city")} className="h-11 bg-white" data-testid="addr-city" />
                    <Input placeholder="State" value={addrForm.state} onChange={setField("state")} className="h-11 bg-white" data-testid="addr-state" />
                    <Input required placeholder="Postal code" value={addrForm.postal_code} onChange={setField("postal_code")} className="h-11 bg-white" data-testid="addr-postal" />
                  </div>
                  <Input required placeholder="Country (US, CA, GB, AU)" maxLength={2} value={addrForm.country} onChange={(e) => setAddrForm((a) => ({ ...a, country: e.target.value.toUpperCase() }))} className="h-11 bg-white" data-testid="addr-country" />
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!addrForm.is_default} onChange={(e) => setAddrForm((a) => ({ ...a, is_default: e.target.checked }))} className="accent-[#8a681c]" data-testid="addr-default" /> Set as default</label>
                  <div className="flex gap-3">
                    <button type="submit" className="h-11 px-6 rounded-xl btn-gold font-semibold" data-testid="addr-save">Save</button>
                    <button type="button" onClick={() => setAddrForm(null)} className="h-11 px-6 rounded-xl border border-border font-semibold" data-testid="addr-cancel">Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}

          {(section === "rewards" || section === "referrals") && (
            <div className="card-soft rounded-2xl p-12 text-center" data-testid={`account-${section}`}>
              <p className="font-display font-bold text-xl capitalize">{section}</p>
              <p className="text-muted-foreground mt-2">Coming soon.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
